import styled from "styled-components";
import SpinnerMini from "../../interface/SpinnerMini";
import { useGetServiceTimes } from "../../hooks/useGetServiceTimes";
import { useGetUserDetails } from "../authentication/useGetUserDetails";
import { useContext, useEffect, useRef, useState } from "react";
import { UserContext } from "../../contexts/UserContext";

const StyledModal = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledDate = styled.h4`
  color: var(--color-grey-400);
`;

const ResourceContainer = styled.div`
  display: flex;
`;

const StyledResourceText = styled.h5`
  font-weight: 50;
  font-size: 0.9em;
`;

const StyledUserImage = styled.img`
  display: flex;
  align-self: center;
  max-width: 50px; /* Adjust as needed */
  max-height: 50px;
  border-radius: 50%;
  margin-right: 10px; /* Gives some space between the image and the text */
`;

const StyledComments = styled.h5`
  font-weight: 100;
  color: var(--color-grey-800);

  overflow-y: auto; /* Enable vertical scrolling */
  max-height: calc(
    1.2em * 10
  ); /* Assuming line-height is 1.2em, adjust as needed */
  line-height: 1.2em; /* Adjust line height as needed */
  display: -webkit-box;
  -webkit-line-clamp: 10; /* Limit the content to 5 lines */
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
`;

const StyledResources = styled.div`
  display: flex;
  flex-direction: column;
  color: var(--color-grey-500);
  font-size: 0.6em;
  white-space: nowrap;
  margin-bottom: 1em;
  align-items: center;

  img {
    max-width: 50px; /* Example size, adjust as needed */
    max-height: 50px;
    border-radius: 50%; /* Optional: makes the image circular */
  }
`;

const StyledTag = styled.div`
  display: flex;
  flex-direction: column;
  color: var(--color-grey-500);
  font-size: 0.6em;
  white-space: nowrap;
  margin-bottom: 1em;
  align-items: center;
`;

function formatUKDate(isoDateString) {
  const date = new Date(isoDateString);
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0"); // getMonth() is zero-based
  const year = date.getFullYear();
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  return `${day}/${month}/${year} ${hours}:${minutes}`;
}

function extractFirstName(wiToolName) {
  // Split the string into parts using the comma as a separator
  const parts = wiToolName.split(",");
  // The first name should be after the comma, which is the second element in the array
  // Trim it to remove any leading or trailing whitespace
  const firstName = parts[1]?.trim();
  return firstName || ""; // If there's no comma or no first name, return an empty string
}

function MaintenanceOrderServiceTime({ orderId }) {
  const userDetailsRef = useRef({});
  const userAccess = useContext(UserContext);
  const [triggerRerender, setTriggerRerender] = useState(false);

  const { data, isLoading, isError, error } = useGetServiceTimes(orderId);
  const { mutate: fetchUserDetails } = useGetUserDetails();

  const witronUser = userAccess?.userDetails?.userCompany === "Witron";

  useEffect(() => {
    async function fetchDetails() {
      if (data && data.data) {
        const resources = data.data.flatMap((stime) => stime.serviceResources);

        for (const resource of resources) {
          if (userDetailsRef.current[resource.resource]) continue;

          await new Promise((resolve, reject) => {
            fetchUserDetails(
              { wiToolName: resource.resource },
              {
                onSuccess: (response) => {
                  if (response.userExists && response.data) {
                    userDetailsRef.current[resource.resource] =
                      response.data.data.userImageBase64;
                  }
                  resolve();
                },
                onError: (error) => {
                  console.error(
                    "Error fetching details:",
                    resource.resource,
                    error
                  );
                  reject(error);
                },
              }
            );
          });
        }
        setTriggerRerender((prev) => !prev);
      }
    }

    fetchDetails();
  }, [data, fetchUserDetails]);

  // Helper to identify resource type
  function addResourceType(resource) {
    return resource.resource.startsWith("_Incomplete:")
      ? { ...resource, type: "tag" }
      : { ...resource, type: "person" };
  }

  // Render Person Component (only for Witron users)
  function renderPerson(resource) {
    return witronUser ? (
      <StyledResources key={resource.resource}>
        <StyledUserImage
          src={
            userDetailsRef.current[resource.resource] ||
            "/images/DefaultUser.png"
          }
          alt={`${extractFirstName(resource.resource)} image`}
        />
        <StyledResourceText>
          {extractFirstName(resource.resource)}
        </StyledResourceText>
        <StyledResourceText>{resource.duration} min.</StyledResourceText>
      </StyledResources>
    ) : null;
  }

  // Render Tag Component (shown to everyone)
  function renderTag(resource) {
    const tagType = resource.resource.split(":")[1].replace(/_/g, " "); // Extract tag type
    return (
      <StyledTag key={resource.resource}>
        <h2 style={{ color: "#dc3545" }}>⚠️ {tagType}</h2>
      </StyledTag>
    );
  }

  // Render Comments for Tags Only (accessible to all users)
  function renderTagComment(comment) {
    return comment ? <StyledComments>{comment}</StyledComments> : null;
  }

  if (isLoading) return <SpinnerMini />;
  if (isError) return <div>Error: {error.message}</div>;

  return (
    <StyledModal>
      {data &&
        data.data.map((stime) => (
          <div key={stime.id}>
            <StyledDate>{formatUKDate(stime.serviceDate)}</StyledDate>
            <ResourceContainer>
              {stime.serviceResources &&
                stime.serviceResources
                  .map(addResourceType) // Add type field to each resource
                  .map((resource) =>
                    resource.type === "person"
                      ? renderPerson(resource)
                      : renderTag(resource)
                  )}
            </ResourceContainer>

            {/* Comments: Show personnel comments only for Witron users */}
            {witronUser && stime.comment && (
              <StyledComments>{stime.comment}</StyledComments>
            )}

            {/* Always show tag comments */}
            {stime.serviceResources &&
              stime.serviceResources.some((res) => res.type === "tag") &&
              renderTagComment(stime.comment)}
          </div>
        ))}
    </StyledModal>
  );
}

export default MaintenanceOrderServiceTime;
